import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
// import LeadCard from '../../components/leads/LeadCard';
import toast from 'react-hot-toast';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';

const LeadsDashboard = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('leads');
  const [forms, setForms] = useState([]);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, hot: 0, warm: 0, cold: 0 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [filterTemperature, setFilterTemperature] = useState(null);
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [sortBy, setSortBy] = useState('recent');

  const tabs = [
    { id: 'leads', label: 'Leads', count: stats.total },
    { id: 'forms', label: 'Forms', count: forms.length },
  ];

  const sortOptions = [
    { value: 'recent', label: 'Most recent' },
    { value: 'name', label: 'Alphabetical' },
    { value: 'hot', label: 'Hottest first' },
  ];

  useEffect(() => {
    if (currentUser?.uid) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const loadData = async () => {
    setLoading(true);
    try {
      const formsRef = collection(db, 'users', currentUser.uid, 'intakeForms');
      const leadsRef = collection(db, 'users', currentUser.uid, 'intakeLeads');

      const [formsSnap, leadsSnap] = await Promise.all([
        getDocs(query(formsRef, orderBy('createdAt', 'desc'))),
        getDocs(query(leadsRef, orderBy('submittedAt', 'desc')))
      ]);

      const loadedForms = formsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setForms(loadedForms);

      const loadedLeads = leadsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setLeads(loadedLeads);

      setStats({
        total: loadedLeads.length,
        hot: loadedLeads.filter(l => l.temperature === 'hot').length,
        warm: loadedLeads.filter(l => l.temperature === 'warm').length,
        cold: loadedLeads.filter(l => l.temperature === 'cold').length,
      });
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = !searchQuery ||
      lead.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTemp = !filterTemperature || lead.temperature === filterTemperature;
    return matchesSearch && matchesTemp;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return (a.name || '').localeCompare(b.name || '');
      case 'hot':
        const tempOrder = { hot: 0, warm: 1, cold: 2 };
        return (tempOrder[a.temperature] || 2) - (tempOrder[b.temperature] || 2);
      default:
        return 0;
    }
  });

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-white dark:bg-black">
        <div className="w-5 h-5 border-2 border-brand border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-black">
      {/* Header */}
      <div className="px-6 pt-5 pb-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          {/* Quick actions */}
          <motion.button
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/app/leads/forms/new')}
            className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-white dark:bg-[#111111] border border-gray-200 dark:border-[#333] text-gray-700 dark:text-gray-200 hover:text-brand hover:border-brand/30 hover:shadow-sm transition-all text-sm font-medium"
          >
            <PlusIcon className="w-5 h-5" />
            <span>New Form</span>
          </motion.button>

          {/* Search */}
          <div className="relative">
            <AnimatePresence mode="wait">
              {showSearch ? (
                <motion.div
                  key="search-input"
                  initial={{ width: 40, opacity: 0 }}
                  animate={{ width: 240, opacity: 1 }}
                  exit={{ width: 40, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="flex items-center"
                >
                  <div className="relative w-full">
                    <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                    <input
                      type="text"
                      autoFocus
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onBlur={() => !searchQuery && setShowSearch(false)}
                      placeholder="Search leads..."
                      className="w-full pl-9 pr-3 py-2 text-sm bg-white dark:bg-[#111111] border border-gray-200 dark:border-[#333] rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 outline-none focus:border-brand/40 dark:focus:border-brand/40 transition-colors"
                    />
                  </div>
                </motion.div>
              ) : (
                <motion.button
                  key="search-icon"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowSearch(true)}
                  className="w-10 h-10 flex items-center justify-center rounded-xl bg-white dark:bg-[#111111] border border-gray-200 dark:border-[#333] text-gray-500 dark:text-gray-400 hover:text-brand hover:border-brand/30 transition-all"
                >
                  <MagnifyingGlassIcon className="w-5 h-5" />
                </motion.button>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Tab navigation */}
      <div className="px-6 pb-3 flex-shrink-0">
        <div className="flex items-center gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`relative px-4 py-2 text-sm transition-all ${
                activeTab === tab.id
                  ? 'text-gray-900 dark:text-white'
                  : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
              }`}
            >
              {activeTab === tab.id && (
                <motion.div
                  layoutId="leads-tab-indicator"
                  className="absolute inset-0 bg-white/70 dark:bg-[#1A1A1A] rounded-lg border border-gray-200/50 dark:border-[#333]"
                  transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                />
              )}
              <span className="relative z-10 font-medium">{tab.label}</span>
              {tab.count > 0 && (
                <span className={`relative z-10 ml-1.5 text-xs ${
                  activeTab === tab.id ? 'text-gray-500 dark:text-gray-400' : 'text-gray-300 dark:text-gray-600'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 px-6 pb-4 min-h-0 flex flex-col">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex-1 min-h-0 bg-gray-50/50 dark:bg-[#0D0D0D] rounded-xl border border-gray-100 dark:border-[#222] flex flex-col overflow-hidden"
        >
          <AnimatePresence mode="wait">
            {activeTab === 'leads' ? (
              <motion.div
                key="leads"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex-1 min-h-0 flex flex-col"
              >
                {leads.length > 0 ? (
                  <>
                    {/* Filter bar */}
                    <div className="px-5 py-2.5 border-b border-gray-100 dark:border-[#222] flex items-center justify-between flex-shrink-0">
                      <div className="flex items-center gap-2">
                        {['all', 'hot', 'warm', 'cold'].map((temp) => (
                          <button
                            key={temp}
                            onClick={() => setFilterTemperature(temp === 'all' ? null : temp)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all capitalize ${
                              (temp === 'all' && !filterTemperature) || filterTemperature === temp
                                ? temp === 'hot' ? 'bg-orange-100 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400' :
                                  temp === 'warm' ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400' :
                                  temp === 'cold' ? 'bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400' :
                                  'bg-gray-200 dark:bg-[#333] text-gray-700 dark:text-gray-200'
                                : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
                            }`}
                          >
                            {temp} {temp !== 'all' && (
                              <span className="ml-1 opacity-60">
                                {temp === 'hot' ? stats.hot : temp === 'warm' ? stats.warm : stats.cold}
                              </span>
                            )}
                          </button>
                        ))}
                      </div>

                      {/* Sort */}
                      <div className="relative">
                        <button
                          onClick={() => setShowSortMenu(!showSortMenu)}
                          className="flex items-center gap-1.5 px-2 py-1 text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded transition-colors"
                        >
                          <span>{sortOptions.find(s => s.value === sortBy)?.label}</span>
                          <ChevronDownIcon className="w-3 h-3" />
                        </button>
                        {showSortMenu && (
                          <div className="absolute right-0 top-full mt-1 bg-white dark:bg-[#1A1A1A] border border-gray-200 dark:border-[#333] rounded-lg shadow-lg py-1 z-10 min-w-[120px]">
                            {sortOptions.map(option => (
                              <button
                                key={option.value}
                                onClick={() => {
                                  setSortBy(option.value);
                                  setShowSortMenu(false);
                                }}
                                className={`w-full px-3 py-1.5 text-left text-xs transition-colors ${
                                  sortBy === option.value
                                    ? 'text-brand'
                                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                                }`}
                              >
                                {option.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Leads list */}
                    <div className="flex-1 min-h-0 overflow-auto divide-y divide-gray-100/60 dark:divide-[#222]">
                      {filteredLeads.length > 0 ? (
                        filteredLeads.map((lead, i) => (
                          <motion.div
                            key={lead.id}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: i * 0.03 }}
                            onClick={() => navigate(`/app/leads/${lead.id}`)}
                            className="flex items-center justify-between px-5 py-4 hover:bg-white/60 dark:hover:bg-[#111111] transition-colors cursor-pointer"
                          >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                lead.temperature === 'hot' ? 'bg-orange-500' :
                                lead.temperature === 'warm' ? 'bg-amber-500' : 'bg-blue-500'
                              }`} />
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                  {lead.name || 'Unknown'}
                                </p>
                                <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                                  {lead.email || 'No email'}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-xs text-gray-400 dark:text-gray-500">
                                {lead.submittedAt?.toDate?.()?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) || 'Recent'}
                              </span>
                            </div>
                          </motion.div>
                        ))
                      ) : (
                        <div className="flex flex-col items-center justify-center py-16 px-6">
                          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">No matching leads</p>
                          <p className="text-xs text-gray-400 dark:text-gray-500">Try adjusting your filters</p>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex flex-col items-center justify-center py-16 px-6">
                    <p className="text-base font-medium text-gray-700 dark:text-gray-200 mb-1">No leads yet</p>
                    <p className="text-sm text-gray-400 dark:text-gray-500 mb-5">Create an intake form to start capturing leads</p>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => navigate('/app/leads/forms/new')}
                      className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-brand hover:bg-brand-hover rounded-full transition-all shadow-sm"
                    >
                      <PlusIcon className="w-4 h-4" />
                      Create Form
                    </motion.button>
                  </div>
                )}
              </motion.div>
            ) : (
              <motion.div
                key="forms"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex-1 min-h-0 flex flex-col"
              >
                {forms.length > 0 ? (
                  <div className="flex-1 min-h-0 overflow-auto divide-y divide-gray-100/60 dark:divide-[#222]">
                    {forms.map((form, i) => (
                      <motion.div
                        key={form.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: i * 0.03 }}
                        onClick={() => navigate(`/app/leads/forms/${form.id}`)}
                        className="flex items-center justify-between px-5 py-4 hover:bg-white/60 dark:hover:bg-[#111111] transition-colors cursor-pointer"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {form.name || 'Untitled Form'}
                          </p>
                          <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                            {form.questions?.length || 0} questions · {form.leadsCount || 0} leads
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${
                            form.isPublished
                              ? 'bg-emerald-50 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400'
                              : 'bg-gray-100 dark:bg-[#333] text-gray-500 dark:text-gray-400'
                          }`}>
                            {form.isPublished ? 'Live' : 'Draft'}
                          </span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="flex-1 flex flex-col items-center justify-center py-16 px-6">
                    <p className="text-base font-medium text-gray-700 dark:text-gray-200 mb-1">No forms yet</p>
                    <p className="text-sm text-gray-400 dark:text-gray-500 mb-5">Create your first intake form</p>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => navigate('/app/leads/forms/new')}
                      className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-brand hover:bg-brand-hover rounded-full transition-all shadow-sm"
                    >
                      <PlusIcon className="w-4 h-4" />
                      Create Form
                    </motion.button>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
};

export default LeadsDashboard;
